<script setup lang="ts">
import { computed, inject, Ref } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { formatDate } from '@/utils/dateUtils';
import DataTable from 'primevue/datatable';
import TableColumn from 'primevue/column';

const dialogRef = inject('dialogRef') as Ref;
const pricingData = dialogRef?.value?.data?.pricingData;

const currentLanguage = getCurrentLocale();

const listHotel = computed(() => handleMapHotel(pricingData));

const handleMapHotel = (data) => {
  const listHotel = [];
  data.forEach((item) => {
    const existingItem = listHotel.find((hotel) => hotel.hotelName === item.hotelName && hotel.duration === item.duration && hotel.checkinDate === item.checkinDate);

    if (existingItem) {
      existingItem.data.push({
        ...item
      });
    } else {
      listHotel.push({
        hotelName: item.hotelName,
        checkinDate: item.checkinDate,
        duration: item.duration,
        data: [
          {
            ...item
          }
        ]
      });
    }
  });

  return listHotel;
};

const handleMapRoomCategory = (listRoom) => {
  const newListRoom = listRoom.reduce((result, item) => {
    const existingRoom = result.find((room) => room[0].roomCode === item.roomCode);

    if (existingRoom) {
      existingRoom.push(item);
    } else {
      result.push([item]);
    }
    return result;
  }, []);

  return newListRoom;
};

const convertTimeStampToDate = (timestamp) => {
  return formatDate(timestamp, 'yyyy-LLL-dd', true, currentLanguage);
};

const getChildColumn = (rooms: any) => {
  const roomChild = rooms.find((room: any) => room.roomType === 'Child');
  return roomChild?.childAgeRanges ?? [];
};
</script>
<template>
  <div class="mt-4">
    <div class="pricing mt-2 mb-4" v-for="(hotel, index) in listHotel" :key="index">
      <div class="detail mt-2">
        <div class="mb-4" v-for="(room, indexRoom) in handleMapRoomCategory(hotel.data)" :key="indexRoom">
          <div class="lead-pricing flex gap-3 justify-content-between">
            <div class="title">
              {{ $t('management.hotel.hotel_name') }} : {{ hotel.hotelName }} ({{ room[0].hoCat }}) - {{ convertTimeStampToDate(hotel.checkinDate) }} {{ hotel.duration ? `( ${hotel.duration} ${$t('proof_pricing.nights')} )` : '' }}
            </div>
            <div class="mr-4 rate-plan">{{ $t('proof_pricing.rate_plan') }} : {{ room[0]?.rateCode }} {{ room[0].interfaceCode && `( ${room[0]?.interfaceCode} )` }}</div>
          </div>
          <DataTable :key="index" :value="room" class="w-full" showGridlines>
            <TableColumn field="roomCatgory" :header="$t('proof_pricing.room_category')" dataType="string"> </TableColumn>
            <TableColumn field="roomType" :header="$t('proof_pricing.type')" dataType="string"> </TableColumn>
            <TableColumn field="currency" :header="$t('proof_pricing.currency')" dataType="string"> </TableColumn>
            <TableColumn field="single" :header="$t('proof_pricing.single')" dataType="string">
              <template #body="{ data, field }">
                <p>{{ data[field]?.toFixed(2) ?? null }}</p>
              </template>
            </TableColumn>
            <TableColumn field="doublePrice" :header="$t('proof_pricing.double')" dataType="string">
              <template #body="{ data, field }">
                <p>{{ data[field]?.toFixed(2) ?? null }}</p>
              </template>
            </TableColumn>
            <TableColumn field="triple" :header="$t('proof_pricing.triple')" dataType="string">
              <template #body="{ data, field }">
                <p>{{ data[field]?.toFixed(2) ?? null }}</p>
              </template>
            </TableColumn>
            <TableColumn field="quad" :header="$t('proof_pricing.quad')" dataType="string">
              <template #body="{ data, field }">
                <p>{{ data[field]?.toFixed(2) ?? null }}</p>
              </template>
            </TableColumn>
            <TableColumn field="fiveInRoom" :header="$t('proof_pricing.5_in_room')" dataType="string">
              <template #body="{ data, field }">
                <p>{{ data[field]?.toFixed(2) ?? null }}</p>
              </template>
            </TableColumn>
            <TableColumn field="childPrices" dataType="string" v-for="(column, indexColumn) in getChildColumn(room)" :key="'childColumn' + indexColumn">
              <template #header>
                <div>
                  <div>{{ $t('common.child') }}</div>
                  <div>{{ column }}</div>
                </div>
              </template>
              <template #body="{ data, field }">
                <p v-if="data.roomType === 'Child'">{{ data[field][indexColumn]?.toFixed(2) ?? null }}</p>
              </template>
            </TableColumn>
            <TableColumn field="infant" :header="$t('common.infant')" dataType="string">
              <template #body="{ data, field }">
                <p>{{ data[field]?.toFixed(2) ?? null }}</p>
              </template>
            </TableColumn>
            <TableColumn field="user" :header="$t('proof_pricing.user')"> </TableColumn>
            <TableColumn field="entryDate" :header="$t('proof_pricing.entry_date')" dataType="date">
              <template #body="{ data }">
                {{ formatDate(data?.entryDate, 'dd-LLL-yyyy', true, currentLanguage) }}
              </template>
            </TableColumn>
            <TableColumn field="time" :header="$t('proof_pricing.time')" dataType="string"> </TableColumn>
          </DataTable>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
:deep() {
  .background-opacity {
    background: rgba($color: #ddd, $alpha: 0.5);
  }

  .min-w-8rem {
    min-width: 8rem;
  }
}
.lead-option {
  background-color: $col-primary;
  color: $col-white;
  padding: 1.05rem 1.7rem;
  font-weight: 700;
}

.lead-promotions,
.lead-pricing {
  background-color: $col-greydk;
  color: $col-white;
  padding: 1.05rem 1.7rem;
  font-weight: 700;
}

.pricing {
  table {
    border-collapse: collapse;
    width: 100%;
  }

  td,
  th {
    border: 1px solid $col-black;
    text-align: left;
    padding: 8px;
    font-weight: 400;
  }

  tr:nth-child(even) {
    background-color: $col-greydk;
  }

  tr:first-child th {
    font-weight: 700;
  }

  tr td:first-child {
    font-weight: 700;
  }

  tr:last-child {
    background-color: $col-black;
    color: $col-white;

    td {
      border: 1px solid $col-white;
    }
  }
}
</style>
